import { ApolloError } from '@apollo/client';
import { loggerUtil, UUID } from '@cmg/common';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import React from 'react';

import usePollingWhenVisible from '../../../../../common/hooks/usePollingWhenVisible';
import { useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess } from '../../../../../common/util/check-access/useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess';
import { isValidCmgEntityKey } from '../../../../../common/util/isValidCmgEntityKey';
import { canSyndicateManagerUpdateInstitutionalIndication } from '../../../../../common/util/offering/syndicate-entitlements';
import { CertificateType, OrderBookConfiguration } from '../../../../../graphql';
import {
  getCertificateType,
  isReg144A,
} from '../../compliance/context/hooks/certificates/Certificates.model';
import { useSyndicateCertificatesModel } from '../../compliance/context/hooks/certificates/syndicate/useCertificatesSyndicateModel';
import {
  Attestation,
  certificateModelDefaults,
  ComplianceWarning,
} from '../../compliance/context/hooks/certificates/useCertificatesModel';
import { OrderBookPersonaType } from '../../hooks/useIdentifyOrderBookPersona';
import { CrmSelection_AllInvestorFirmsMappedToCmgEntityKeyQuery } from '../components/crm-selection/graphql/__generated__';
import { useInvestorFirmModel } from './hooks/investorFirm/useInvestorFirmModel';
import {
  SyndicateIndication_PublishedOfferings_OfferingPartsFragment,
  SyndicateIndicationQuery,
  useSyndicateIndicationQuery,
} from './hooks/syndicate/__generated__';

type AccessChecks = {
  canReadOfferingAndIndication: boolean;
  canUpdateIndication: boolean;
  canBillingAndDeliveryAgentUpdateAllocation: boolean;
};

export type IndicationActivitySyndicateContextType = {
  offeringId: UUID;
  loading: boolean;
  error?: ApolloError;
  basicPublishedOffering?: SyndicateIndicationQuery['basicPublishedOffering'];
  publishedOffering: SyndicateIndication_PublishedOfferings_OfferingPartsFragment | null;
  finalInstitutionalAllocationSet?: Exclude<
    SyndicateIndicationQuery['finalInstitutionalAllocationSet'],
    undefined
  >;
  complianceWarning: ComplianceWarning;
  attestation: Attestation | null;
  allInvestorFirmsMappedToCmgEntityKey?: CrmSelection_AllInvestorFirmsMappedToCmgEntityKeyQuery['allInvestorFirmsMappedToCmgEntityKey'];
  access: AccessChecks;
  certificateType: CertificateType | null;
  orderBookConfiguration: Pick<
    OrderBookConfiguration,
    | 'isLimitIndicationsOnlyEnabled'
    | 'isIndicationPriceAsPercentageParEnabled'
    | 'isOnlySharesUnitOnIndicationsEnabled'
  >;
};

export const IndicationActivitySyndicateContext =
  React.createContext<IndicationActivitySyndicateContextType>({
    offeringId: '',
    publishedOffering: null,
    loading: false,
    complianceWarning: certificateModelDefaults.warning,
    attestation: null,
    access: {
      canReadOfferingAndIndication: false,
      canBillingAndDeliveryAgentUpdateAllocation: false,
      canUpdateIndication: false,
    },
    certificateType: null,
    orderBookConfiguration: {
      isLimitIndicationsOnlyEnabled: false,
      isOnlySharesUnitOnIndicationsEnabled: false,
      isIndicationPriceAsPercentageParEnabled: false,
    },
  });

export const useIndicationActivitySyndicateContext = () => {
  const context = React.useContext(IndicationActivitySyndicateContext);

  if (isNil(context)) {
    throw new Error('No provider found for IndicationActivitySyndicateContext');
  }

  return context;
};

export type IndicationActivitySyndicateContextProps = {
  offeringId: UUID;
  cmgEntityKey: UUID | undefined;
  indicationId: UUID;
};

export const IndicationActivitySyndicateContextProvider: React.FC<
  React.PropsWithChildren<IndicationActivitySyndicateContextProps>
> = ({ children, offeringId, cmgEntityKey, indicationId }) => {
  const {
    canRead: canReadOfferingAndIndication,
    canUpdate: canUpdateIndication,
    loading: indicationAccessLoading,
  } = useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess({
    offeringId,
  });
  const {
    loading: isLoadingOffering,
    data,
    error,
    startPolling,
    stopPolling,
    called,
  } = useSyndicateIndicationQuery({
    variables: {
      offeringId,
      // cmgEntityKey context being actually firmKey means investor not paired to any cmg entity
      cmgEntityKey: isValidCmgEntityKey(cmgEntityKey!) ? cmgEntityKey : null,
      indicationId,
    },
    skip: !cmgEntityKey || !offeringId || !canReadOfferingAndIndication,
    fetchPolicy: 'cache-and-network',
  });
  const offering = defaultTo(data?.publishedOfferings?.items?.[0], null);
  const instruments = offering?.instruments ?? [];
  const offeringType = offering?.type;
  const isReg144AValue = instruments[0] ? isReg144A(instruments[0]) : false;
  const certificateType = getCertificateType(offeringType, isReg144AValue);

  usePollingWhenVisible({ pollInterval: 5_000, startPolling, stopPolling, called });

  if (error) {
    loggerUtil.error(new Error('Error in IndicationActivitySyndicateContextProvider'), {
      error,
      offeringId,
      cmgEntityKey,
      indicationId,
      url: window.location.href,
    });
  }

  const {
    loading: isLoadingCertificates,
    warning: complianceWarning,
    attestation,
  } = useSyndicateCertificatesModel({
    type: OrderBookPersonaType.SYNDICATE,
    offeringId,
    cmgEntityKey,
    certificateType,
    offeringType,
  });
  const { loading: isLoadingInvestorFirm, allInvestorFirmsMappedToCmgEntityKey } =
    useInvestorFirmModel({
      type: OrderBookPersonaType.SYNDICATE,
      cmgEntityKey,
    });

  const context = React.useMemo<IndicationActivitySyndicateContextType>(
    () => ({
      offeringId,
      loading:
        isLoadingOffering ||
        isLoadingCertificates ||
        indicationAccessLoading ||
        isLoadingInvestorFirm,
      error,
      basicPublishedOffering: data?.basicPublishedOffering,
      publishedOffering: defaultTo(offering, null),
      finalInstitutionalAllocationSet: data?.finalInstitutionalAllocationSet,
      complianceWarning,
      attestation,
      allInvestorFirmsMappedToCmgEntityKey,
      access: {
        canReadOfferingAndIndication,
        canUpdateIndication,
        canBillingAndDeliveryAgentUpdateAllocation:
          canSyndicateManagerUpdateInstitutionalIndication({
            cmgEntityKey:
              data?.basicPublishedOffering.orderBook.institutionalIndication
                ?.billingAndDeliveryAgentCmgEntityKey,
            syndicatesEntitlements: offering?.syndicate?.managers ?? [],
          }),
      },
      certificateType,
      orderBookConfiguration: {
        isLimitIndicationsOnlyEnabled:
          !!offering?.orderBookConfiguration?.isLimitIndicationsOnlyEnabled,
        isIndicationPriceAsPercentageParEnabled:
          !!offering?.orderBookConfiguration?.isIndicationPriceAsPercentageParEnabled,
        isOnlySharesUnitOnIndicationsEnabled:
          !!offering?.orderBookConfiguration?.isOnlySharesUnitOnIndicationsEnabled,
      },
    }),
    [
      allInvestorFirmsMappedToCmgEntityKey,
      attestation,
      canReadOfferingAndIndication,
      canUpdateIndication,
      certificateType,
      complianceWarning,
      data?.basicPublishedOffering,
      data?.finalInstitutionalAllocationSet,
      error,
      indicationAccessLoading,
      isLoadingCertificates,
      isLoadingInvestorFirm,
      isLoadingOffering,
      offering,
      offeringId,
    ]
  );

  if (context.error) {
    loggerUtil.error(new Error('Error in IndicationActivitySyndicateContextProvider context'), {
      error: context.error,
      offeringId,
      cmgEntityKey,
      url: window.location.href,
    });
  }

  return (
    <IndicationActivitySyndicateContext.Provider value={context}>
      {children}
    </IndicationActivitySyndicateContext.Provider>
  );
};
